// src/components/CarrierDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import { useTranslation } from "react-i18next";
import "../styles/CarrierDetails.css";

// Country mapping for flags
const countryMapping = {
  Austria: "at",
  Belgium: "be",
  Croatia: "hr",
  "Czech Republic": "cz",
  Denmark: "dk",
  Estonia: "ee",
  Finland: "fi",
  France: "fr",
  Germany: "de",
  Hungary: "hu",
  Italy: "it",
  Netherlands: "nl",
  Norway: "no",
  Poland: "pl",
  Portugal: "pt",
  Spain: "es",
  Sweden: "se",
  Switzerland: "ch",
  "United Kingdom": "gb",
};

// Phone number country codes mapping
const phoneNumberMapping = {
  "+43": "at",
  "+32": "be",
  "+385": "hr",
  "+420": "cz",
  "+45": "dk",
  "+372": "ee",
  "+358": "fi",
  "+33": "fr",
  "+49": "de",
  "+36": "hu",
  "+39": "it",
  "+31": "nl",
  "+47": "no",
  "+48": "pl",
  "+351": "pt",
  "+34": "es",
  "+46": "se",
  "+41": "ch",
  "+44": "gb",
};

// Currency flags
const currencyFlags = {
  EUR: "https://upload.wikimedia.org/wikipedia/commons/b/b7/Flag_of_Europe.svg",
  USD: "https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg",
  GBP: "https://upload.wikimedia.org/wikipedia/en/a/a3/Flag_of_the_United_Kingdom.svg",
  CHF: "https://upload.wikimedia.org/wikipedia/commons/f/f3/Flag_of_Switzerland.svg",
  PLN: "https://upload.wikimedia.org/wikipedia/en/1/12/Flag_of_Poland.svg",
  NOK: "https://upload.wikimedia.org/wikipedia/commons/d/d9/Flag_of_Norway.svg",
  SEK: "https://upload.wikimedia.org/wikipedia/en/4/4c/Flag_of_Sweden.svg",
};

const getCountryFlag = (countryName) => {
  if (!countryName) return "";
  const code = countryMapping[countryName];
  return code ? `https://flagcdn.com/w40/${code}.png` : "";
};

const getPhoneFlag = (phoneCode) => {
  if (!phoneCode) return "";
  const code = phoneNumberMapping[phoneCode];
  return code ? `https://flagcdn.com/w40/${code}.png` : "";
};

const getCurrencyFlagUrl = (currencyCode) => (!currencyCode ? "" : (currencyFlags[currencyCode] || ""));
const toFileUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `http://localhost:5000/${p}`);

function CarrierDetails() {
  // ✅ useTranslation com o namespace do ficheiro: "carrier_details"
  const { t } = useTranslation("carrier_details");

  const { carrierId } = useParams();
  const navigate = useNavigate();
  const [carrier, setCarrier] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`/carriers/${carrierId}`);
        setCarrier(data);
      } catch (error) {
        console.error("Error fetching carrier details:", error);
        setErrorMessage(t("failed"));
      }
    })();
  }, [carrierId, t]);

  if (!carrier) return <div>{t("loading")}</div>;

  const notSpecified = t("values.not_specified");
  const paymentMethods = Array.isArray(carrier.paymentMethodsAccepted)
    ? carrier.paymentMethodsAccepted.join(", ")
    : carrier.paymentMethodsAccepted || notSpecified;

  const invoiceMethods = Array.isArray(carrier.invoiceDeliveryMethod)
    ? carrier.invoiceDeliveryMethod.join(", ")
    : carrier.invoiceDeliveryMethod || notSpecified;

  return (
    <div className="carrier-details-container">
      <div className="carrier-details-back-arrow" onClick={() => navigate("/clients?view=carriers")}>
        {t("back")}
      </div>

      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <h2 className="carrier-details-section-title-1">{t("title")}</h2>

      <div className="carrier-details-columns">
        {/* Column 1 */}
        <div className="carrier-details-column">
          <div className="carrier-details-section-title">{t("sections.basic_info")}</div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.company_name")}</span>
            <span className="carrier-details-value">{carrier.companyName}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.fleet_size")}</span>
            <span className="carrier-details-value">{carrier.fleetSize}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.truck_types")}</span>
            <span className="carrier-details-value">{carrier.truckTypes}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.service_areas")}</span>
            <span className="carrier-details-value">{carrier.serviceAreas}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.load_capacity")}</span>
            <span className="carrier-details-value">
              {carrier.loadCapacityPerTruck} {t("values.tons_suffix")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.drivers_employed")}</span>
            <span className="carrier-details-value">{carrier.driversEmployed}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.services_offered")}</span>
            <span className="carrier-details-value">{carrier.servicesOffered}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.partnership_history")}</span>
            <span className="carrier-details-value">{carrier.partnershipHistory}</span>
          </div>
        </div>

        {/* Column 2 */}
        <div className="carrier-details-column">
          <div className="carrier-details-section-title">{t("sections.address_contact")}</div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.address")}</span>
            <span className="carrier-details-value">{carrier.address}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.city")}</span>
            <span className="carrier-details-value">{carrier.city}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.postal_code")}</span>
            <span className="carrier-details-value">{carrier.postalCode}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.country")}</span>
            <span className="carrier-details-value">
              {carrier.country}
              {carrier.country && (
                <img src={getCountryFlag(carrier.country)} alt={carrier.country} className="carrier-details-flag" />
              )}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.main_contact_name")}</span>
            <span className="carrier-details-value">{carrier.mainContactName}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.main_contact_title")}</span>
            <span className="carrier-details-value">{carrier.mainContactTitle}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.contact_email")}</span>
            <span className="carrier-details-value">{carrier.contactEmail}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.phone_country_code")}</span>
            <span className="carrier-details-value">
              {carrier.phoneNumberCountryCode}
              {carrier.phoneNumberCountryCode && (
                <img
                  src={getPhoneFlag(carrier.phoneNumberCountryCode)}
                  alt={carrier.phoneNumberCountryCode}
                  className="carrier-details-flag"
                />
              )}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.phone_number")}</span>
            <span className="carrier-details-value">{carrier.phoneNumber}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.website")}</span>
            <span className="carrier-details-value">{carrier.website}</span>
          </div>
        </div>

        {/* Column 3 */}
        <div className="carrier-details-column">
          <div className="carrier-details-section-title">{t("sections.insurance_license")}</div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.community_license")}</span>
            <span className="carrier-details-value">
              {carrier.communityLicense ? (
                <a href={toFileUrl(carrier.communityLicense)} target="_blank" rel="noopener noreferrer">
                  {t("values.view_license")}
                </a>
              ) : t("values.not_uploaded")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.community_license_expiry")}</span>
            <span className="carrier-details-value">
              {carrier.communityLicenseExpiry
                ? new Date(carrier.communityLicenseExpiry).toLocaleDateString()
                : t("values.not_specified")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.cmr_insurance")}</span>
            <span className="carrier-details-value">
              {carrier.cmrInsurance ? (
                <a href={toFileUrl(carrier.cmrInsurance)} target="_blank" rel="noopener noreferrer">
                  {t("values.view_insurance")}
                </a>
              ) : t("values.not_uploaded")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.cmr_insurance_expiry")}</span>
            <span className="carrier-details-value">
              {carrier.cmrInsuranceExpiry
                ? new Date(carrier.cmrInsuranceExpiry).toLocaleDateString()
                : t("values.not_specified")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.motor_tpl_insurance")}</span>
            <span className="carrier-details-value">
              {carrier.motorThirdPartyLiabilityInsurance ? (
                <a
                  href={toFileUrl(carrier.motorThirdPartyLiabilityInsurance)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("values.view_insurance")}
                </a>
              ) : t("values.not_uploaded")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.motor_tpl_insurance_expiry")}</span>
            <span className="carrier-details-value">
              {carrier.motorThirdPartyLiabilityInsuranceExpiry
                ? new Date(carrier.motorThirdPartyLiabilityInsuranceExpiry).toLocaleDateString()
                : t("values.not_specified")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.comprehensive_vehicle_insurance")}</span>
            <span className="carrier-details-value">
              {carrier.comprehensiveVehicleInsurance ? (
                <a href={toFileUrl(carrier.comprehensiveVehicleInsurance)} target="_blank" rel="noopener noreferrer">
                  {t("values.view_insurance")}
                </a>
              ) : t("values.not_uploaded")}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.comprehensive_vehicle_insurance_expiry")}</span>
            <span className="carrier-details-value">
              {carrier.ComprehensiveVehicleInsuranceExpiryDate
                ? new Date(carrier.ComprehensiveVehicleInsuranceExpiryDate).toLocaleDateString()
                : t("values.not_specified")}
            </span>
          </div>
        </div>

        {/* Column 4 */}
        <div className="carrier-details-column">
          <div className="carrier-details-section-title">{t("sections.bank_payment")}</div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.bank_name")}</span>
            <span className="carrier-details-value">{carrier.bankName}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.account_holder_name")}</span>
            <span className="carrier-details-value">{carrier.accountHolderName}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.account_number")}</span>
            <span className="carrier-details-value">{carrier.accountNumber}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.iban")}</span>
            <span className="carrier-details-value">{carrier.iban}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.swift_bic")}</span>
            <span className="carrier-details-value">{carrier.swift_bic}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.currency")}</span>
            <span className="carrier-details-value">
              {getCurrencyFlagUrl(carrier.currency) && (
                <img src={getCurrencyFlagUrl(carrier.currency)} alt={carrier.currency} className="carrier-details-flag" />
              )}
              {carrier.currency}
            </span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.payment_methods_accepted")}</span>
            <span className="carrier-details-value">{paymentMethods}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.payment_due_date")}</span>
            <span className="carrier-details-value">{carrier.paymentDueDate || notSpecified}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.discount_terms")}</span>
            <span className="carrier-details-value">{carrier.discountTerms || notSpecified}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.late_payment_penalties")}</span>
            <span className="carrier-details-value">{carrier.latePaymentPenalties || notSpecified}</span>
          </div>

          <div className="carrier-details-section-title" style={{ marginTop: 20 }}>
            {t("sections.invoicing_prefs")}
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.vat_tax_information")}</span>
            <span className="carrier-details-value">{carrier.VAT_TaxInformation || notSpecified}</span>
          </div>

          <div className="carrier-details-field-row">
            <span className="carrier-details-label">{t("labels.invoice_delivery_method")}</span>
            <span className="carrier-details-value">{invoiceMethods}</span>
          </div>
        </div>
      </div>

      <div className="carrier-details-full-width-field-2">
        <div className="carrier-details-section-title-2" style={{ marginBottom: 6 }}>
          {t("sections.notes")}
        </div>
        <div className="carrier-details-value">
          {carrier.additionalNotes || t("values.no_notes")}
        </div>
      </div>

      <button
        className="carrier-details-edit-btn"
        onClick={() => navigate(`/clients/edit/carrier/${carrierId}`, { state: { prefill: carrier } })}
      >
        {t("actions.edit_carrier")}
      </button>
    </div>
  );
}

export default CarrierDetails;
